import { useEffect } from 'react';

/**
 * Polls a fetcher function at the given interval AND refetches immediately
 * when the browser tab becomes visible or the window regains focus.
 *
 * Why both? Browsers aggressively throttle (or pause) setInterval on
 * backgrounded tabs, so a pure interval-only approach leaves users staring
 * at stale data when they return to the tab. The visibility + focus
 * listeners close that gap.
 *
 * @param {Function} fetcher      function to call. Should be wrapped in
 *                                useCallback so its reference is stable.
 * @param {number}   intervalMs   poll cadence (default 20000).
 * @param {boolean}  enabled      set false to temporarily disable polling.
 */
export function usePolling(fetcher, intervalMs = 20000, enabled = true) {
  useEffect(() => {
    if (!enabled || !fetcher) return undefined;
    fetcher();
    const id = intervalMs ? setInterval(fetcher, intervalMs) : null;
    const onVisible = () => { if (document.visibilityState === 'visible') fetcher(); };
    const onFocus = () => fetcher();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      if (id) clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetcher, intervalMs, enabled]);
}
